import { ArrowDownRight, MapPin, Sparkles } from "lucide-react";
import Logo from "./Logo";
import { useBooking } from "./BookingDialog";

const HERO_IMG =
  "https://images.unsplash.com/photo-1630595271375-5073a6c0638b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxzcGElMjBhZXN0aGV0aWNpYW4lMjBuZXV0cmFsfGVufDB8fHx8MTc3ODI4NzYyMnww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { openBooking } = useBooking();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 bg-[#0F1F4E] overflow-hidden grain"
    >
      {/* Subtle gold halos */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-[#EAD584] blur-3xl opacity-15" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 w-[30rem] h-[30rem] rounded-full bg-[#1B3370] blur-3xl opacity-70" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Big script logo as the hero mark */}
        <div className="flex flex-col items-center text-center reveal in-view">
          <div className="overline-gold mb-6 inline-flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#EAD584]" />
            Sacramento &middot; esthetician
            <span className="inline-block w-8 h-px bg-[#EAD584]" />
          </div>
          <div data-testid="hero-logo" className="animate-shimmer-gold">
            <Logo tone="gold" size="xl" showFrame showSubtitle />
          </div>
        </div>

        {/* Tagline + CTA + image */}
        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="col-span-12 lg:col-span-7">
            <h1
              data-testid="hero-title"
              className="font-serif text-[2.75rem] sm:text-6xl lg:text-7xl leading-[1] tracking-tighter font-light text-[#F8F1D8]"
            >
              Self-care, <span className="italic text-[#EAD584]">softly</span><br />
              rendered.
            </h1>
            <p
              data-testid="hero-description"
              className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#D6D6BD] font-light"
            >
              A welcoming wellness space in downtown Sacramento. Personalized
              massage and custom facials &mdash; every treatment shaped
              around the body, the skin, and the moment you&rsquo;re in.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={() => openBooking()}
                data-testid="hero-book-now"
                className="btn-shine inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm tracking-wider uppercase text-[#0F1F4E] bg-[#EAD584] hover:bg-[#F0DC8A] transition-colors"
              >
                Book a Treatment <ArrowDownRight className="w-4 h-4" />
              </button>
              <a
                href="#services"
                data-testid="hero-explore-services"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-full text-sm tracking-wider uppercase text-[#EAD584] border border-[#EAD584] hover:bg-[#EAD584] hover:text-[#0F1F4E] transition-colors"
              >
                Explore the menu
              </a>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-lg">
              <div data-testid="hero-stat-1">
                <div className="font-serif text-3xl text-[#EAD584]">7+</div>
                <div className="overline-gold opacity-80">years of practice</div>
              </div>
              <div data-testid="hero-stat-2">
                <div className="font-serif text-3xl text-[#EAD584]">1:1</div>
                <div className="overline-gold opacity-80">private studio</div>
              </div>
              <div data-testid="hero-stat-3" className="col-span-2 sm:col-span-1">
                <div className="font-serif text-3xl text-[#EAD584]">&infin;</div>
                <div className="overline-gold opacity-80">customised every visit</div>
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#EAD584] -z-10 opacity-30 animate-float-slow" />
              <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-[#1B3370] -z-10 animate-float-slow [animation-delay:2s]" />
              <img
                src={HERO_IMG}
                alt="Beauty & Balance by Bella — wellness studio in downtown Sacramento"
                data-testid="hero-image"
                fetchPriority="high"
                width="640"
                height="800"
                className="w-full h-[460px] md:h-[560px] object-cover rounded-lg border border-[#EAD584]/30"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-[#0A1838]/70 backdrop-blur-md border border-[#EAD584]/30 rounded-md px-5 py-4 flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-[#EAD584]" />
                <span className="overline-gold">Now welcoming new clients</span>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-2 text-[#D6D6BD] text-sm">
              <MapPin className="w-4 h-4 text-[#EAD584]" /> Downtown Sacramento &amp; surrounding areas
            </div>
          </div>
        </div>
      </div>

      {/* Marquee ribbon */}
      <div className="relative mt-20 md:mt-28 border-y border-[#EAD584]/30 py-5 overflow-hidden bg-[#0A1838]">
        <div className="flex animate-marquee whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0 items-center">
              {[
                "Therapeutic Massage",
                "Custom Facials",
                "Signature Glow · Grand Ritual",
                "Licensed Esthetician",
                "Locally rooted, lovingly delivered",
              ].map((t, idx) => (
                <span key={idx} className="flex items-center">
                  <span className="font-serif italic text-2xl md:text-3xl text-[#EAD584] px-8">
                    {t}
                  </span>
                  <span className="w-2 h-2 rounded-full bg-[#EAD584]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
